import React, { useContext } from 'react';
import { FlowContext } from '../../core/flow-engine';
import { RewardsContext } from '../../context/RewardsContext/RewardsContext';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import {
  CartContextProvider,
  CartContext
} from '../../context/CartContext/CartContext';
import { getRewardsAvailable } from '../../flows/shared/summit.utils';
import {
  changeMinimumInvestment,
  decideFundMinimumProperty,
  formatFunds,
  autoSplitPercents,
  isNonFundedFlow,
  isTaxable
} from '../../flows/shared/utils';
import styles from './style.css';
import { MFBeaconButton } from '../../components/Beacon/BeaconComponents';
import Heading from '../../components/Typography/Heading/Heading';
import SubHeading from '../../components/Typography/SubHeading/SubHeading';
import AllocationTable from '../../components/AllocationTable/AllocationTable';
import useCartDetailsState from '../../hooks/useCartDetailsState';
import PortfolioAllocation from '../../components/PortfolioAllocation/PortfolioAllocation';
import { dollarMask } from '../../core/util';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';

export const InvestmentReviewContent = () => {
  const { next, appState, flowProps } = useContext(FlowContext);

  const { currentTier = 0 } = useContext(RewardsContext) || {};

  const {
    store: { accountType, amount, app, inReviewMode, loadedData = {} }
  } = appState;
  const { trust, trustee } = app;
  const { fundMinimum } = flowProps;

  const { shoppingFunds } = loadedData;

  const rewardsAvailable = getRewardsAvailable(
    accountType,
    trust,
    trustee,
    currentTier
  );

  const cartState = useContext(CartContext);

  const { selectedFunds = [], totalAmount, data = {} } = cartState;
  const { mutualFunds = [] } = data;

  // TODO: create an option to retrieve fund minimums
  // by UGMAUTMA account type on the server side
  // so we don't have to mutate data in the UI
  const mutualFundsWithAllocations = changeMinimumInvestment(
    mutualFunds,
    fundMinimum
  );

  const {
    validCart,
    validTaxableCart,
    totalAllocationsValid,
    fundsWithCategories,
    categoryTotals: { overallBonds, overallStocks, overallShortTerm }
  } = useCartDetailsState({
    selectedFunds,
    mutualFundsWithAllocations,
    rewardsAvailable,
    totalAmount
  });

  const validCartForFlow = decideFundMinimumProperty(
    accountType,
    !validCart,
    !validTaxableCart
  );

  const subTitle = (
    <span>
      {isNonFundedFlow(accountType)
        ? `Your initial investment will be allocated to the fund(s) you've selected.`
        : `Your $${dollarMask(amount)} will be allocated to these fund(s)`}
    </span>
  );

  return (
    <div>
      <Heading text="Review your portfolio allocation" />
      <SubHeading text={subTitle} />
      <div className="my-portfolio">
        <div className="table">
          <AllocationTable
            fundsWithCategories={fundsWithCategories}
            shoppingCartEmpty={!fundsWithCategories.length}
            rewardsAvailable={rewardsAvailable}
            isTaxable={isTaxable(accountType)}
            handleAddMoreFundsClick={next(
              fundsWithCategories,
              'reselect-funds'
            )}
          />
        </div>
        {totalAllocationsValid && (
          <div className="chart">
            <PortfolioAllocation
              shoppingCartEmpty={!fundsWithCategories.length}
              stocks={overallStocks}
              bonds={overallBonds}
              shortTerm={overallShortTerm}
            />
          </div>
        )}
      </div>

      <div className="button-container">
        <MFBeaconButton
          disabled={validCartForFlow}
          id="investmentReviewContinue"
          data-name={generateGeneralTestID('FundReview', 'ContinueButton')}
          variant="primary"
          className="beacon-button-margin-override"
          size="normal"
          onClick={next(fundsWithCategories)}
          label={inReviewMode ? 'Continue to Confirmation' : 'Continue'}
        />
        {shoppingFunds && (
          <MFBeaconButton
            id="investmentReviewReselect"
            data-name={generateGeneralTestID(
              'FundReview',
              'ReselectFundsButton'
            )}
            variant="secondary"
            className="beacon-button-margin-override"
            size="normal"
            onClick={next(fundsWithCategories, 'reselect-funds')}
            label="Re-select investments"
          />
        )}
        <MFBeaconButton
          id="investmentReviewBack"
          data-name={generateGeneralTestID('FundReview', 'BackButton')}
          variant="secondary"
          className="beacon-button-margin-override"
          size="normal"
          onClick={next(fundsWithCategories, 'updateAndBack')}
          label="Back"
        />
      </div>

      <PhoneNumber />
      <style jsx>{styles}</style>
    </div>
  );
};

const InvestmentReviewScreen = ({ appState }) => {
  const {
    store: { chosenInvestments, amount, loadedData = {} }
  } = appState;

  const funds =
    !chosenInvestments?.length && loadedData.shoppingFunds
      ? autoSplitPercents(loadedData.shoppingFunds, amount)
      : chosenInvestments;

  const formattedSelectedFunds = formatFunds(funds);

  return (
    <CartContextProvider
      initialTotalAmount={amount}
      initialPercentMode
      initialFunds={formattedSelectedFunds}>
      <InvestmentReviewContent />
    </CartContextProvider>
  );
};

export default InvestmentReviewScreen;
